/**
 * Inventory expiration dates (Mimesa). The app captures one date per product in the store
 * check's inventory step: `inventory[].expirationDate`, 'YYYY-MM-DD'. Pure helpers, so the
 * thresholds are unit-tested without rendering.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const DATE_FORMAT = new Intl.DateTimeFormat('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });

/**
 * The calendar date of `value` as UTC midnight, or null. Accepts 'YYYY-MM-DD' and ISO date-times
 * (only the date part is used, so a time zone never moves the day).
 * @param {any} value
 * @returns {Date|null}
 */
export function parseExpirationDate(value) {
    if (typeof value !== 'string') return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
    if (!match) return null;
    const [, y, m, d] = match.map(Number);
    const date = new Date(Date.UTC(y, m - 1, d));
    // Reject impossible dates such as 2026-02-31 (Date.UTC would roll them over).
    if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
    return date;
}

/** `value` as dd/mm/aaaa (es-VE), or '' when it is not a date. */
export function formatExpirationDate(value) {
    const date = parseExpirationDate(value);
    return date ? DATE_FORMAT.format(date) : '';
}

function todayUtc(now) {
    return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * Where an expiration date stands, compared with `now`'s local calendar day:
 *   { status: 'expired', days }   days < 0 (days since it expired, negative)
 *   { status: 'warning', days }   0 ≤ days ≤ warningDays
 *   { status: 'ok', days }        later than that
 * null when there is no valid date.
 * @param {any} value
 * @param {{ now?: Date, warningDays?: number }} [options]
 */
export function expirationStatus(value, { now = new Date(), warningDays = 30 } = {}) {
    const date = parseExpirationDate(value);
    if (!date) return null;
    const days = Math.round((date.getTime() - todayUtc(now)) / DAY_MS);
    if (days < 0) return { status: 'expired', days };
    if (days <= warningDays) return { status: 'warning', days };
    return { status: 'ok', days };
}

/** Short text for the tag next to the date, or '' when no tag is shown. */
export function expirationTagText(state) {
    if (!state) return '';
    if (state.status === 'expired') return 'Vencido';
    if (state.status === 'warning') {
        if (state.days === 0) return 'Vence hoy';
        return state.days === 1 ? 'Vence mañana' : `Vence en ${state.days} días`;
    }
    return '';
}
